import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Détection de fautes de frappe courantes dans le domaine de l'e-mail.
const COMMON_DOMAIN_FIX = {
  "gmial.com": "gmail.com", "gmai.com": "gmail.com", "gmail.co": "gmail.com",
  "gmil.com": "gmail.com", "gnail.com": "gmail.com", "gmaill.com": "gmail.com",
  "gamil.com": "gmail.com", "gmail.cm": "gmail.com", "gmail.con": "gmail.com",
  "hotmial.com": "hotmail.com", "hotmai.com": "hotmail.com", "hotmail.co": "hotmail.com",
  "hotmal.com": "hotmail.com", "otmail.com": "hotmail.com", "hotmail.fr ": "hotmail.fr",
  "yahou.fr": "yahoo.fr", "yaho.fr": "yahoo.fr", "yahoo.co": "yahoo.com", "yaoo.fr": "yahoo.fr",
  "outlok.com": "outlook.com", "outllook.com": "outlook.com", "outook.com": "outlook.com",
  "wanadoo.com": "wanadoo.fr", "orange.com": "orange.fr", "laposte.com": "laposte.net",
  "free.com": "free.fr", "sfr.com": "sfr.fr",
};

function suggestEmailCorrection(email) {
  const e = (email || "").trim().toLowerCase();
  const at = e.indexOf("@");
  if (at < 1 || e.indexOf(" ") >= 0) return "";
  const local = e.slice(0, at);
  const domain = e.slice(at + 1);
  if (!domain) return "";
  if (COMMON_DOMAIN_FIX[domain]) return `${local}@${COMMON_DOMAIN_FIX[domain]}`;
  return "";
}
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lightbulb, CheckCircle2, Gift, Mail, Sparkles, Heart, Store, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const PROFILES = ["Visiteur", "Utilisateur / Bénéficiaire", "Professionnel / Commerçant", "CSE / Association"];

const TYPES = [
  "Un commerce / artisan",
  "Un professionnel",
  "Une association",
  "Un club",
  "Un CSE / comité d'entreprise",
  "Une amélioration du site",
];

const PLACEHOLDERS = {
  "Un commerce / artisan": "Nom du commerce, ville, ce qu'il propose… et pourquoi il mériterait d'être sur PROXIreduc-28.",
  "Un professionnel": "Nom du professionnel, activité, ville… dites-nous pourquoi le recommander.",
  "Une association": "Nom de l'association, commune, son action locale…",
  "Un club": "Nom du club, sport ou activité, commune…",
  "Un CSE / comité d'entreprise": "Nom de l'entreprise / du CSE et ce que vous aimeriez y retrouver.",
  "Une amélioration du site": "Quelle fonctionnalité ou amélioration rendrait PROXIreduc-28 encore plus utile pour vous ?",
};

export default function Suggestions() {
  const [sp] = useSearchParams();
  const initialType = TYPES.includes(sp.get("type")) ? sp.get("type") : "";
  const [form, setForm] = useState({ email: "", name: "", city: "", profile: "Visiteur", suggestion_type: initialType, message: "", gdpr_consent: true });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  useSeo({
    title: "Votre suggestion — PROXIreduc-28",
    description: "Partagez votre idée pour améliorer PROXIreduc-28. On la lit, on vous répond, et on vous crée un accès gratuit aux bons plans locaux.",
    canonicalPath: "/suggestions",
  });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/suggestions", { ...form, origin_page: window.location.pathname });
      setDone(r.data || { ok: true });
      toast.success("Merci ! Votre suggestion a bien été envoyée.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'envoyer votre suggestion.");
    } finally { setLoading(false); }
  };

  if (done) {
    const created = done.account_created;
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="suggestion-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
          <CheckCircle2 size={32} />
        </div>
        <h1 className="mt-5 font-heading text-3xl font-extrabold text-slate-900">Merci infiniment ! 💡</h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Votre idée a bien été transmise à notre équipe. Nous la lisons avec attention et vous
          répondrons directement à <strong>{form.email}</strong>.
        </p>
        {created ? (
          <div className="mt-6 bg-orange-50 border border-orange-200 rounded-2xl p-5 text-left" data-testid="suggestion-account-created">
            <div className="flex items-center gap-2 font-bold text-[#E05638]"><Gift size={18} /> Votre cadeau de bienvenue</div>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              Pour vous remercier, nous venons de vous créer un <strong>accès gratuit</strong> à tous les
              bons plans de vos commerçants locaux. Vos identifiants viennent de vous être envoyés par e-mail —
              pensez à vérifier vos spams.
            </p>
            <Link to="/connexion">
              <Button className="mt-4 btn-shine text-white h-11 w-full" data-testid="suggestion-go-login">
                <Heart size={16} className="mr-2" /> Je découvre mes bons plans
              </Button>
            </Link>
          </div>
        ) : (
          <Link to="/">
            <Button variant="outline" className="mt-6 h-11" data-testid="suggestion-go-home">Retour à l'accueil</Button>
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="suggestions-page">
      {/* Bandeau incitatif */}
      <div className="rounded-3xl bg-gradient-to-br from-[#E05638] to-[#f0943a] text-white p-7 md:p-9 shadow-lg shadow-orange-200/50">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-xs font-bold uppercase tracking-widest">
          <Sparkles size={14} /> Votre avis construit PROXIreduc-28
        </span>
        <h1 className="mt-4 font-heading text-3xl md:text-4xl font-extrabold leading-tight flex items-center gap-2">
          <Lightbulb size={30} className="shrink-0" /> Faites grandir — et grandir mieux — PROXIreduc-28.
        </h1>
        <p className="mt-3 text-white/90 text-base md:text-lg max-w-xl">
          Deux façons d'aider : <strong>suggérez un acteur local</strong> (commerce, professionnel, association,
          club, comité d'entreprise) qui mériterait d'y figurer, ou <strong>proposez une amélioration du site</strong>.
          On lit tout, on vous répond, et on vous offre en retour un <strong>accès gratuit</strong> aux réductions de
          vos commerçants locaux. 🎁
        </p>
      </div>

      <form onSubmit={submit} className="mt-7 bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="suggestion-form">
        <div data-testid="suggestion-type-picker">
          <Label className="flex items-center gap-1.5"><Store size={14} className="text-[#E05638]" /> Que souhaitez-vous suggérer ?</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {TYPES.map((t) => {
              const active = form.suggestion_type === t;
              const isImprove = t === "Une amélioration du site";
              return (
                <button
                  type="button"
                  key={t}
                  onClick={() => setForm((s) => ({ ...s, suggestion_type: active ? "" : t }))}
                  data-testid={`suggestion-type-${t.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                    active
                      ? "bg-[#E05638] text-white border-[#E05638] shadow-sm shadow-orange-200"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#E05638]/60 hover:text-[#E05638]"
                  }`}
                >
                  {isImprove ? <Rocket size={13} /> : <Store size={13} />} {t}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><Mail size={14} className="text-[#E05638]" /> Votre email <span className="text-[#E05638]">*</span></Label>
          <Input type="email" required value={form.email} onChange={set("email")} placeholder="vous@email.fr" data-testid="suggestion-email" />
          {(() => {
            const fix = suggestEmailCorrection(form.email);
            return fix ? (
              <p className="mt-1 text-xs text-amber-700" data-testid="suggestion-email-hint">
                Vouliez-vous dire{" "}
                <button type="button" className="font-semibold underline hover:text-amber-900"
                        onClick={() => setForm((s) => ({ ...s, email: fix }))} data-testid="suggestion-email-fix">{fix}</button>
                {" "}? Une adresse incorrecte ne recevra pas notre réponse.
              </p>
            ) : null;
          })()}
          <p className="mt-1 text-xs text-slate-500">C'est à cette adresse que nous vous répondrons et vous enverrons vos accès.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Prénom / Nom (facultatif)</Label><Input value={form.name} onChange={set("name")} data-testid="suggestion-name" /></div>
          <div><Label>Ville (facultatif)</Label><Input value={form.city} onChange={set("city")} data-testid="suggestion-city" /></div>
        </div>
        <div>
          <Label>Vous êtes… (facultatif)</Label>
          <Select value={form.profile} onValueChange={(v) => setForm((s) => ({ ...s, profile: v }))}>
            <SelectTrigger data-testid="suggestion-profile"><SelectValue /></SelectTrigger>
            <SelectContent>{PROFILES.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Votre suggestion <span className="text-[#E05638]">*</span></Label>
          <Textarea rows={5} required value={form.message} onChange={set("message")} placeholder={PLACEHOLDERS[form.suggestion_type] || "Dites-nous tout : un acteur local à ajouter, ou une idée pour améliorer PROXIreduc-28…"} data-testid="suggestion-message" />
        </div>

        <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer hover:border-slate-300" data-testid="suggestion-gdpr-wrapper">
          <input type="checkbox" required checked={form.gdpr_consent}
            onChange={(e) => setForm((s) => ({ ...s, gdpr_consent: e.target.checked }))}
            className="mt-1 w-4 h-4" data-testid="suggestion-gdpr-optin" />
          <span className="text-xs text-slate-600 leading-relaxed">
            J'accepte de recevoir la réponse avec cet email et qu'un compte gratuit soit créé pour recevoir ma
            réponse. Mes données sont traitées conformément à la{" "}
            <a href="/confidentialite" className="text-[#E05638] underline">politique de confidentialité</a>
            {" "}(droit d'accès, de rectification et de suppression à tout moment).
          </span>
        </label>

        <Button type="submit" disabled={loading || !form.gdpr_consent} className="w-full btn-shine text-white h-12 text-base font-extrabold" data-testid="suggestion-submit">
          {loading ? "Envoi…" : "Envoyer ma suggestion & recevoir mon accès"}
        </Button>
        <p className="text-center text-xs text-slate-400">Gratuit, sans engagement — merci de faire grandir le réseau local 🙌</p>
      </form>
    </div>
  );
}
